import { create } from 'zustand'

import type { AskYogiConnector } from 'features/ask-yogi/service'
import {
  type AskYogiToolPreferences,
  askYogiToolPreferencesService,
} from 'features/ask-yogi/service/tool-preferences'

const getPersistedPreferences = (
  conversationId?: string,
): AskYogiToolPreferences =>
  conversationId
    ? (askYogiToolPreferencesService.getConversationPreferences(
        conversationId,
      ) ?? askYogiToolPreferencesService.getGlobalPreferences())
    : askYogiToolPreferencesService.getGlobalPreferences()

const persistPreferences = ({
  conversationId,
  webSearchEnabled,
  selectedConnectors,
}: {
  conversationId: string | undefined
  webSearchEnabled: boolean
  selectedConnectors: ReadonlySet<AskYogiConnector>
}): void => {
  const preferences: AskYogiToolPreferences = {
    webSearchEnabled,
    selectedConnectors: Array.from(selectedConnectors),
  }

  if (conversationId) {
    askYogiToolPreferencesService.setConversationPreferences(
      conversationId,
      preferences,
    )
  } else {
    askYogiToolPreferencesService.setGlobalPreferences(preferences)
  }
}

export type AskYogiUIState = {
  activeChatbotConversation?: string
  setActiveChatbotConversation: (id: string | undefined) => void
  webSearchEnabled: boolean
  setWebSearchEnabled: (enabled: boolean) => void
  selectedConnectors: ReadonlySet<AskYogiConnector>
  setConnectorSelected: (slug: AskYogiConnector, selected: boolean) => void
  requestSavePromptMenu: boolean
  setRequestSavePromptMenu: (value: boolean) => void
  /**
   * Question text to drop into the composer of ONE specific conversation.
   *
   * Forking navigates away from the conversation that set it, so the seed has to
   * outlive that screen and wait here for the fork to arrive. It names its target
   * because the source conversation's composer is still mounted at that moment:
   * an unscoped draft would be typed into the conversation being forked FROM.
   */
  pendingForkDraft?: { conversationUuid: string; text: string }
  setPendingForkDraft: (
    draft: { conversationUuid: string; text: string } | undefined,
  ) => void
  resetAskYogiUI: () => void
}

const buildInitialState = (): Pick<
  AskYogiUIState,
  | 'activeChatbotConversation'
  | 'selectedConnectors'
  | 'webSearchEnabled'
  | 'pendingForkDraft'
> => {
  const preferences = getPersistedPreferences()
  return {
    activeChatbotConversation: undefined as string | undefined,
    // Named explicitly: `set` MERGES, so anything omitted here survives
    // `resetAskYogiUI` — which is what a project switch calls.
    pendingForkDraft: undefined,
    webSearchEnabled: preferences.webSearchEnabled,
    selectedConnectors: new Set<AskYogiConnector>(
      preferences.selectedConnectors,
    ),
  }
}

const initialState = buildInitialState()

export const useAskYogiUIStore = create<AskYogiUIState>()((set) => ({
  ...initialState,
  requestSavePromptMenu: false,

  setActiveChatbotConversation: (id) => {
    const preferences = getPersistedPreferences(id)
    set({
      activeChatbotConversation: id,
      webSearchEnabled: preferences.webSearchEnabled,
      selectedConnectors: new Set(preferences.selectedConnectors),
    })
  },

  setWebSearchEnabled: (enabled) =>
    set((state) => {
      persistPreferences({
        conversationId: state.activeChatbotConversation,
        webSearchEnabled: enabled,
        selectedConnectors: state.selectedConnectors,
      })
      return { webSearchEnabled: enabled }
    }),

  setConnectorSelected: (slug, selected) =>
    set((state) => {
      const selectedConnectors = new Set(state.selectedConnectors)
      if (selected) {
        selectedConnectors.add(slug)
      } else {
        selectedConnectors.delete(slug)
      }
      persistPreferences({
        conversationId: state.activeChatbotConversation,
        webSearchEnabled: state.webSearchEnabled,
        selectedConnectors,
      })
      return { selectedConnectors }
    }),

  setRequestSavePromptMenu: (value) => set({ requestSavePromptMenu: value }),

  setPendingForkDraft: (draft) => set({ pendingForkDraft: draft }),

  resetAskYogiUI: () => set(buildInitialState()),
}))
